import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Users, HeartHandshake, MapPin, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#FFF7E6]">
      <header className="max-w-lg mx-auto px-6 pt-8 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-2xl bg-[#F97316] flex items-center justify-center shadow-md"><ShieldCheck className="w-5 h-5 text-white" /></div>
          <span className="text-lg font-bold font-head">HumNest <span className="text-[#F97316]">AI</span></span>
        </div>
        <Link to={user ? "/home" : "/auth/sign-in"} data-testid="landing-signin" className="text-sm font-bold text-[#111827]/70">{user ? "Open app" : "Sign in"}</Link>
      </header>

      <main className="max-w-lg mx-auto px-6 pt-10 pb-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-block text-xs font-bold uppercase tracking-wider bg-[#C4B5FD]/40 text-[#4c1d95] px-3 py-1 rounded-full">India</span>
          <h1 className="text-4xl sm:text-5xl font-black font-head tracking-tight mt-5 leading-[1.1]">When every second matters, <span className="text-[#F97316]">HumNest</span> coordinates the people who can help.</h1>
          <p className="text-lg text-[#111827]/70 mt-5 leading-relaxed">Restore trust, reconnect neighbours, and make sure no one faces an emergency alone.</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-8 rounded-3xl overflow-hidden shadow-lg border border-[#111827]/5">
          <img src="https://images.unsplash.com/photo-1512676052261-98bab4919138?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBuZWlnaGJvcmhvb2QlMjBjb21tdW5pdHl8ZW58MHx8fHwxNzgzNjQzNzc3fDA&ixlib=rb-4.1.0&q=85" alt="Neighbourhood community" className="w-full h-56 object-cover" />
        </motion.div>

        <div className="grid gap-3 mt-8">
          {[{ i: Users, t: "Your neighbourhood, connected", d: "Coordinate with verified neighbours and responders." },
            { i: HeartHandshake, t: "Care Circle", d: "Keep loved ones safe with a trusted circle." },
            { i: MapPin, t: "Privacy first", d: "No continuous tracking. Location only when you share it." }].map((f, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-5 flex items-start gap-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#111827]/5">
              <div className="w-11 h-11 rounded-xl bg-[#F97316]/10 flex items-center justify-center shrink-0"><f.i className="w-5 h-5 text-[#F97316]" /></div>
              <div><h3 className="font-bold text-[#111827]">{f.t}</h3><p className="text-[#111827]/60 text-sm mt-0.5">{f.d}</p></div>
            </div>
          ))}
        </div>

        <div className="mt-10 space-y-3">
          <Link to="/auth/sign-up" data-testid="landing-get-started" className="w-full bg-[#F97316] text-white rounded-2xl py-4 font-bold text-lg shadow-md flex items-center justify-center gap-2 min-h-[56px] hover:-translate-y-0.5 transition-transform">Get started <ArrowRight className="w-5 h-5" /></Link>
          <Link to="/auth/sign-in" className="w-full bg-[#C4B5FD]/20 text-[#111827] rounded-2xl py-4 font-semibold flex items-center justify-center min-h-[56px]">I already have an account</Link>
        </div>

        <p className="text-xs text-[#111827]/50 text-center mt-8 leading-relaxed">HumNest is in active development and does not replace police, ambulance, fire, or other official emergency services.</p>
        <div className="flex justify-center gap-4 mt-4 text-xs font-semibold text-[#111827]/50">
          <Link to="/legal/terms">Terms</Link><Link to="/legal/privacy">Privacy</Link><Link to="/legal/beta-safety">Safety</Link>
        </div>
      </main>
    </div>
  );
}
